import ShapeNote from './ShapeNote';
import ShapeRect from './ShapeRect';
import ShapeRoundRect from './ShapeRoundRect';
import ShapeCircle from './ShapeCircle';
import ShapeText from './ShapeText';
import ShapeTriangle from './ShapeTriangle';
import ShapeDiamond from './ShapeDiamond';
import ShapeStar from './ShapeStar';
import ShapeCylinder from './ShapeCylinder';
import ShapeOpenBracket from './ShapeOpenBracket';
import ShapeClosedBracket from './ShapeClosedBracket';
import ShapeCloud from './ShapeCloud';
import ShapeCertificate from './ShapeCertificate';
import ShapePentagon from './ShapePentagon';
import ShapeRecord from './ShapeRecord';
import ShapeImage from './ShapeImage';
import ShapeVideo from './ShapeVideo';
import ShapeOctagon from './ShapeOctagon';
import ShapePredefinedProcess from './ShapePredefinedProcess';
import ShapeParallelogram from './ShapeParallelogram';
import ShapeTrapezoid from './ShapeTrapezoid';
import ShapeProcessEnd from './ShapeProcessEnd';
import ShapeEntity from './ShapeEntity';
import ShapeWeakEntity from './ShapeWeakEntity';
import ShapeWeakRelationship from './ShapeWeakRelationship';
import ShapeMultivaluedAttribute from './ShapeMultivaluedAttribute';
import ShapeCallOutBox from './ShapeCallOutBox';
import ShapeTimeline from './ShapeTimeline';
import ShapeIcon from './ShapeIcon';
import ShapeSpriteIcon from './ShapeSpriteIcon';
import ShapeAnnotation from './ShapeAnnotation';
import ShapeProgressBar from './ShapeProgressBar';
import ShapeMilestone from './ShapeMilestone';
import { fabric } from 'fabric';
import ShapeCustomPath from './ShapeCustomPath';
import ShapeIntegrationRecord from './ShapeIntegrationRecord';
import { businessProcessShapeClasses } from '../businessProcess/shapes';
import ShapeSmartRecord from './ShapeSmartRecord';
import ShapeArrow from './ShapeArrow';
import ShapeCircleArrow from './ShapeCircleArrow';
import ShapeDocument from './ShapeDocument';
import ShapeSubroutine from './ShapeSubroutine';
import ShapeOffpageConnector from './ShapeOffpageConnector';
import ShapeManualInput from './ShapeManualInput';
import ShapePreparation from './ShapePreparation';

export const Shapes = {
  annotation: ShapeAnnotation,
  arrow: ShapeArrow,
  callOutBox: ShapeCallOutBox,
  certificate: ShapeCertificate,
  circle: ShapeCircle,
  circleArrow: ShapeCircleArrow,
  closedBracket: ShapeClosedBracket,
  cloud: ShapeCloud,
  customPath: ShapeCustomPath,
  cylinder: ShapeCylinder,
  diamond: ShapeDiamond,
  entity: ShapeEntity,
  integrationRecord: ShapeIntegrationRecord,
  icon: ShapeIcon,
  spriteIcon: ShapeSpriteIcon,
  image: ShapeImage,
  milestone: ShapeMilestone,
  multivaluedAttribute: ShapeMultivaluedAttribute,
  note: ShapeNote,
  octagon: ShapeOctagon,
  openBracket: ShapeOpenBracket,
  parallelogram: ShapeParallelogram,
  pentagon: ShapePentagon,
  predefinedProcess: ShapePredefinedProcess,
  processEnd: ShapeProcessEnd,
  progressBar: ShapeProgressBar,
  record: ShapeRecord,
  rect: ShapeRect,
  roundRect: ShapeRoundRect,
  star: ShapeStar,
  text: ShapeText,
  timeline: ShapeTimeline,
  trapezoid: ShapeTrapezoid,
  triangle: ShapeTriangle,
  video: ShapeVideo,
  weakEntity: ShapeWeakEntity,
  weakRelationship: ShapeWeakRelationship,
  smartRecord: ShapeSmartRecord,
  document: ShapeDocument,
  subroutine: ShapeSubroutine,
  offpageConnector: ShapeOffpageConnector,
  preparation: ShapePreparation,
  manualInput: ShapeManualInput,

  ...businessProcessShapeClasses,
} as const;

export type ShapeName = keyof typeof Shapes;

export const sanitizeShapeName = (shapeName: string): ShapeName => {
  if (!(shapeName in Shapes)) {
    console.error(`Unknown shape ${shapeName}`);
    return 'rect';
  }
  return shapeName as ShapeName;
};

function shapeNameToClass(shapeName: ShapeName) {
  if (!(shapeName in Shapes)) {
    throw new Error(`Unknown shape ${shapeName}`);
  }

  return Shapes[shapeName];
}

export type CoordsArray = [string, ...number[]];

// Simplified version of Fabric's path parsing that doesn't have special arc
// handling since we don't use SVG's arc format. (Converting from SVG arcs to
// canvas arcs is unnecessaily complex and slow.)
function parsePath(pathString: string) {
  const result: CoordsArray[] = [];
  const coords: string[] = [];
  // @ts-expect-error accessing fabric internals here
  const re = fabric.rePathCommand;
  let currentPath: string;
  let parsed: number;
  let match: string;
  let coordsStr: string;

  if (!pathString || !pathString.match) {
    return result;
  }
  const path = pathString.match(/[mzlhvcsqtar][^mzlhvcsqtar]*/gi)!;

  for (let i = 0, coordsParsed: CoordsArray, len = path.length; i < len; i++) {
    currentPath = path[i];

    coordsStr = currentPath.slice(1).trim();
    coords.length = 0;

    const command = currentPath.charAt(0);
    coordsParsed = [command];

    while ((match = re.exec(coordsStr))) {
      coords.push(match[0]);
    }

    for (let j = 0, jlen = coords.length; j < jlen; j++) {
      parsed = parseFloat(coords[j]);
      if (!isNaN(parsed)) {
        coordsParsed.push(parsed);
      }
    }

    result.push(coordsParsed);
  }

  return result;
}

function computeRoundedCornerPath(
  obj: fabric.Object,
  x: number,
  y: number,
  r: number,
  quadrant: number,
  uniform?: number
) {
  // 90 degree rounded corner that adapts to the scale. Args: radius, quadrant (0=tr, 1=br, 2=bl, 3=tl), uniform
  // if uniform is true, the radius will stay the same if the shape is scaled up. Otherwise, it'll scale with
  // the smaller of the two dimensions.
  // Note that it only draws clockwise arcs.
  const w = obj.width;
  const h = obj.height;
  let endX: number, endY: number;
  let c1x: number, c1y: number, c2x: number, c2y: number;
  const K = 0.552; // Bezier curve control point offset. From https://stackoverflow.com/a/37837742

  // uniform radius only applies when the object is scaled up. If the user scales it down,
  // we need to scale down the radius too or things get weird.
  const scaleX = uniform ? Math.min(obj.scaleX, 1) : obj.scaleX;
  const scaleY = uniform ? Math.min(obj.scaleY, 1) : obj.scaleY;
  const radius = Math.min(w * scaleX, h * scaleY) * r;

  const rx = radius / obj.scaleX;
  const ry = radius / obj.scaleY;
  const offsetX = Math.max(0, w * r - rx);
  const offsetY = Math.max(0, h * r - ry);
  const points: number[] = [];

  switch (quadrant) {
    case 0:
      x += offsetX / w;
      endX = x * w + rx;
      endY = y * h + ry;
      c1x = x * w + rx * K;
      c1y = y * h;
      c2x = endX;
      c2y = endY - ry * K;
      break;

    case 1:
      y += offsetY / h;
      endX = x * w - rx;
      endY = y * h + ry;
      c1x = x * w;
      c1y = y * h + ry * K;
      c2x = endX + rx * K;
      c2y = endY;
      break;

    case 2:
      x -= offsetX / w;
      endX = x * w - rx;
      endY = y * h - ry;
      c1x = x * w - rx * K;
      c1y = y * h;
      c2x = endX;
      c2y = endY + ry * K;
      break;

    case 3:
      y -= offsetY / h;
      endX = x * w + rx;
      endY = y * h - ry;
      c1x = x * w;
      c1y = y * h - ry * K;
      c2x = endX - rx * K;
      c2y = endY;
      break;
    default:
      throw new Error('Invalid quadrant');
  }
  points.push(x * w, y * h);
  points.push(c1x, c1y);
  points.push(c2x, c2y);
  points.push(endX, endY);

  switch (quadrant) {
    case 0:
      endY += offsetY / h;
      break;
    case 1:
      endX -= offsetX / w;
      break;
    case 2:
      endY -= offsetY / h;
      break;
    case 3:
      endX += offsetX / w;
      break;
  }
  points.push(endX, endY);

  return points;
}

const makeSimplePath = (pathString: string) =>
  fabric.util.makePathSimpler(parsePath(pathString));

export {
  shapeNameToClass,
  parsePath,
  computeRoundedCornerPath,
  makeSimplePath,
};
